import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, Shield } from "lucide-react";
import logoImage from "@assets/favicon new no background cc6633.png";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { getCsrfHeaders } from "@/lib/queryClient";
import { useLocation } from "wouter";

export default function CompletePaymentSetup() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  // Free tier users skip payment setup entirely
  useEffect(() => {
    if (user && user.membershipTier === "Free") {
      // Mark payment setup as complete for free users so the gate doesn't re-trigger
      fetch("/api/complete-free-setup", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
      }).catch(console.error);
      setLocation("/dashboard");
    }
  }, [user?.membershipTier]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSetupPayment = async () => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/create-setup-session", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify({ billingCycle: user?.billingCycle || 'monthly', membershipTier: user?.membershipTier || 'Paid' }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Could not create payment setup session");
    } catch (err) {
      console.error("Failed to create setup session:", err);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
     <div className="flex-1 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <a href="/">
              <img src={logoImage} alt="Fill The Trucks" className="h-20" />
            </a>
          </div>
          <CardTitle className="text-2xl" data-testid="text-payment-setup-title">Complete Your Signup</CardTitle>
          <CardDescription className="text-base">
            One last step — we need a credit card on file to activate your subscription and complete your registration.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Secure Payment</p>
                <p className="text-sm text-muted-foreground">Your card is stored securely with Stripe. Cancel anytime.</p>
              </div>
            </div>
          </div>

          <Button
            onClick={handleSetupPayment}
            disabled={isLoading}
            className="w-full"
            size="lg"
            data-testid="button-complete-payment-setup"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {isLoading ? "Setting up..." : "Add Credit Card"}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By adding your card, you agree to our billing terms. You're in full control — cancel anytime from your account settings.
          </p>
        </CardContent>
      </Card>
    </div>
    </div>
  );
}